import React, { createContext, useContext, useEffect, useMemo, ReactNode } from 'react';
import { CssVarsProvider, extendTheme, useColorScheme } from '@mui/joy/styles';
import CssBaseline from '@mui/joy/CssBaseline';

const STORAGE_KEY = 'adminThemeMode';

const adminTheme = extendTheme({
  colorSchemes: {
    light: {
      palette: {
        primary: {
          50: '#eef4ff',
          100: '#dbe7ff',
          200: '#bcd1ff',
          300: '#90b3ff',
          400: '#638bff',
          500: '#3f6bf2',
          600: '#2f55cf',
          700: '#2745a6',
          800: '#233d83',
          900: '#1f3568'
        },
        background: {
          body: '#f4f6fb',
          surface: '#ffffff'
        }
      }
    },
    dark: {
      palette: {
        primary: {
          50: '#0d1224',
          100: '#101937',
          200: '#162247',
          300: '#1d2f63',
          400: '#2a4290',
          500: '#3f6bf2',
          600: '#5b85ff',
          700: '#86a4ff',
          800: '#b1c5ff',
          900: '#dde6ff'
        },
        background: {
          body: '#0b0f1a',
          surface: '#121826'
        }
      }
    }
  },
  fontFamily: {
    body: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    display: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  }
});

type Mode = 'light' | 'dark';

interface ThemeCtx {
  mode: Mode;
  toggle: () => void;
}

const Ctx = createContext<ThemeCtx | undefined>(undefined);

function ThemeBridge({ children }: { children: ReactNode }) {
  const { mode, setMode } = useColorScheme();
  const current: Mode = (mode === 'dark' ? 'dark' : 'light');

  const toggle = () => {
    const next: Mode = current === 'dark' ? 'light' : 'dark';
    setMode(next);
    localStorage.setItem(STORAGE_KEY, next);
  };

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY) as Mode | null;
    if (saved && saved !== mode) setMode(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => ({ mode: current, toggle }), [current]); // eslint-disable-line react-hooks/exhaustive-deps
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function AdminThemeProvider({ children }: { children: ReactNode }) {
  const initial = (localStorage.getItem(STORAGE_KEY) as Mode | null) || 'light';
  return (
    <CssVarsProvider theme={adminTheme} defaultMode={initial} modeStorageKey={STORAGE_KEY}>
      <CssBaseline />
      <ThemeBridge>{children}</ThemeBridge>
    </CssVarsProvider>
  );
}

export function useAdminTheme(): ThemeCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAdminTheme must be used within AdminThemeProvider');
  return ctx;
}
